import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { getAxiosInstance } from '../../utils/axiosInstance';
import { GRADIENTS, COLORS } from './themeConstants';
import { Toaster, toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

const steps = [
	{
		title: 'Basic Information',
		fields: [
			{ name: 'collegeName', label: 'Name of the Institution', type: 'text' },
			{ name: 'shortName', label: 'Short Name of the Institution', type: 'text' },
			{ name: 'slogan', label: 'Slogan of the Institution', type: 'text' },
			{ name: 'establishedYear', label: 'Established Year', type: 'text' },
			{ name: 'founder', label: 'Enter the Name of the Founder', type: 'text' },
			{ name: 'principalName', label: 'Enter the Name of the Principal', type: 'text' },
			{ name: 'accreditation', label: 'Accreditation Grade', type: 'text' },
			{ name: 'university', label: 'University of Affiliation', type: 'text' },
			{
				name: 'logo',
				label: 'Institution Logo (PNG format)',
				type: 'file',
				accept: '.png',
				buttonText: 'Choose Logo',
			},
		],
	},
	{
		title: 'Location & Contact Details',
		fields: [
			{ name: 'phoneNumber', label: 'Phone Number', type: 'tel' },
			{ name: 'email', label: 'Email Address', type: 'email' },
			{ name: 'website', label: 'Website URL', type: 'url' },
			{ name: 'address', label: 'Address', type: 'text' },
			{ name: 'district', label: 'District', type: 'text' },
			{ name: 'pincode', label: 'Pincode', type: 'text' },
			{ name: 'area', label: 'Area of the Campus', type: 'text' },
			{
				name: 'campusType',
				label: 'Campus Type',
				type: 'select',
				options: ['Urban', 'Rural'],
			},
		],
	},
	{
		title: 'Organizational Details',
		fields: [
			{
				name: 'organization',
				label: 'Organizational Body Running the Institution',
				type: 'select',
				options: ['Trust', 'Government', 'Co-operate'],
			},
			{
				name: 'institutionType',
				label: 'Type of Institution',
				type: 'select',
				options: ['aided', 'self', 'govt'],
			},
			{
				name: 'affiliatedType',
				label: 'Affiliated Type',
				type: 'select',
				options: ['Affiliated', 'Autonomous', 'University'],
			},
			{
				name: 'genderType',
				label: 'Gender Type of Education',
				type: 'select',
				options: ['Mixed', 'Boys', 'Girls'],
			},
			{
				name: 'educationLevels',
				label: 'Level(s) of Education',
				type: 'select',
				options: ['Kindergarden', 'Lower Primary', 'Upper Primary', 'High School', 'Higher Secondary Education', 'Under Graduation', 'Post Graduation', 'Doctor of Philosophy', 'Diploma', 'Teacher Education'],
			},
			{
				name: 'educationCategory',
				label: 'Category of Education',
				type: 'select',
				options: ['Arts and Science', 'Engineering', 'Polytechnic', 'Medical', 'Law', 'Commerce', 'ITI'],
			},
		],
	},
	{
		title: 'Verification',
		fields: [{ name: 'otp', label: 'Enter OTP', type: 'text' }],
	},
];

function RegisterForm() {
	const history = useHistory();
	const [currentStep, setCurrentStep] = useState(0);
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [showGuide, setShowGuide] = useState(false);

	const handleInputChange = (e) => {
		const { name, value, type, checked, files } = e.target;
		if (type === 'file') {
			const file = files[0];
			if (file && name === 'logo') {
				const reader = new FileReader();
				reader.onloadend = () => {
					setFormData((prevData) => ({
						...prevData,
						[name]: file,
						[`${name}Preview`]: reader.result,
					}));
				};
				reader.readAsDataURL(file);
			}
		} else if (type === 'checkbox') {
			setFormData((prevData) => {
				const updatedValue = prevData[name] || [];
				if (checked) {
					return { ...prevData, [name]: [...updatedValue, value] };
				} else {
					return { ...prevData, [name]: updatedValue.filter((item) => item !== value) };
				}
			});
		} else {
			setFormData({ ...formData, [name]: value });
		}
		console.log(formData);
	};

	const sendOTP = async () => {
		try {
			const instance = getAxiosInstance();
			const response = await instance.post('/auth/newCollege-send-otp', {
				phoneNumber: formData.phoneNumber,
			});
			setFormData({ ...formData, token: response.data.token });
			toast.success('OTP sent successfully!');
		} catch (error) {
			toast.error('Failed to send OTP');
		}
	};

	const handleSubmit = async () => {
		if (validateFinal()) {
			setLoading(true);
			try {
				const instance = getAxiosInstance();
				await instance.post('/register/registerCollege', {
					collegeName: formData.collegeName,
					shortName: formData.shortName,
					slogan: formData.slogan,
					establishedYear: formData.establishedYear,
					founder: formData.founder,
					principalName: formData.principalName,
					accreditation: formData.accreditation,
					university: formData.university,
					logo: formData.logo,
					phoneNumber: formData.phoneNumber,
					email: formData.email,
					website: formData.website,
					address: formData.address,
					district: formData.district,
					pincode: formData.pincode,
					area: formData.area,
					campusType: formData.campusType,
					organization: formData.organization,
					institutionType: formData.institutionType,
					affiliatedType: formData.affiliatedType,
					genderType: formData.genderType,
					educationLevels: formData.educationLevels,
					educationCategory: formData.educationCategory,
					otp: formData.otp,
					token: formData.token,
				});
				toast.success('Registration successful!');
				setTimeout(() => (window.location.href = '/'), 2000);
			} catch (error) {
				toast.error(error.response?.data?.message || 'Registration failed');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleBackToHome = () => {
		history.push('/');
	};

	const validateStep = () => {
		const newErrors = {};
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const phoneRegex = /^[0-9]{10}$/;

		if (currentStep === 0) {
			if (!formData.collegeName) newErrors.collegeName = 'College Name is required';
			if (!formData.shortName) newErrors.shortName = 'Short Name is required';
			if (!formData.slogan) newErrors.slogan = 'Slogan is required';
			if (!formData.establishedYear) newErrors.establishedYear = 'Established Year is required';
			if (!formData.founder) newErrors.founder = 'Founder Name is required';
			if (!formData.principalName) newErrors.principalName = 'Principal Name is required';
			if (!formData.accreditation) newErrors.accreditation = 'Accreditation Grade is required';
			if (!formData.university) newErrors.university = 'University of Affiliation is required';
			if (!formData.logo) newErrors.logo = 'Logo is required';
		} else if (currentStep === 1) {
			if (!formData.phoneNumber) newErrors.phoneNumber = 'Mobile Number is required';
			else if (!phoneRegex.test(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid phone number format';
			if (!formData.email) newErrors.email = 'College Email is required';
			else if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email format';
			if (!formData.website) newErrors.website = 'Website is required';
			if (!formData.address) newErrors.address = 'College Address is required';
			if (!formData.district) newErrors.district = 'District is required';
			if (!formData.pincode) newErrors.pincode = 'Pincode is required';
			if (!formData.area) newErrors.area = 'Area is required';
			if (!formData.campusType) newErrors.campusType = 'Campus Type is required';
		} else if (currentStep === 2) {
			if (!formData.organization) newErrors.organization = 'Organization Type is required';
			if (!formData.institutionType) newErrors.institutionType = 'Institution Type is required';
			if (!formData.affiliatedType) newErrors.affiliatedType = 'Affiliated Type is required';
			if (!formData.genderType) newErrors.genderType = 'Gender Type is required';
			if (!formData.educationLevels) newErrors.educationLevels = 'Education Levels is required';
			if (!formData.educationCategory) newErrors.educationCategory = 'Education Category is required';
		} else if (currentStep === 3) {
			if (!formData.otp) newErrors.otp = 'OTP is required';
		}

		setErrors(newErrors);
		console.log(newErrors);

		return Object.keys(newErrors).length === 0;
	};

	const validateFinal = () => {
		const newErrors = {};
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const phoneRegex = /^[0-9]{10}$/;

		if (!formData.collegeName) newErrors.collegeName = 'College Name is required';
		if (!formData.shortName) newErrors.shortName = 'Short name is required';
		if (!formData.slogan) newErrors.slogan = 'Slogan is required';
		if (!formData.establishedYear) newErrors.establishedYear = 'Established Year';
		if (!formData.founder) newErrors.founder = 'Founder Name is required';
		if (!formData.principalName) newErrors.principalName = 'Principal Name is required';
		if (!formData.accreditation) newErrors.accreditation = 'Accreditation Grade is required';
		if (!formData.university) newErrors.university = 'Name of University of Affiliation is required';
		if (!formData.logo) newErrors.logo = 'Logo is required';
		if (!formData.phoneNumber) newErrors.phoneNumber = 'Mobile Number is required';
		else if (!phoneRegex.test(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid phone number format';
		if (!formData.email) newErrors.email = 'College Email is required';
		else if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email format';
		if (!formData.website) newErrors.website = 'Website is required';
		if (!formData.address) newErrors.address = 'College Address is required';
		if (!formData.district) newErrors.district = 'District is required';
		if (!formData.pincode) newErrors.pincode = 'Pincode is required';
		if (!formData.area) newErrors.area = 'Area is required';
		if (!formData.campusType) newErrors.campusType = 'Campus Type is required';
		if (!formData.organization) newErrors.organization = 'Organization Type is required';
		if (!formData.institutionType) newErrors.institutionType = 'Institution Type is required';
		if (!formData.affiliatedType) newErrors.affiliatedType = 'Affiliated Type is required';
		if (!formData.genderType) newErrors.genderType = 'Gender Type is required';
		if (!formData.educationLevels) newErrors.educationLevels = 'Education Levels is required';
		if (!formData.educationCategory) newErrors.educationCategory = 'Education Category is required';
		if (!formData.otp) newErrors.otp = 'OTP is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const nextStep = () => {
		if (validateStep()) {
			if (currentStep === 1) sendOTP();
			setCurrentStep((prev) => prev + 1);
		}
	};

	return (
		<div className={`relative min-h-screen flex flex-col items-center justify-start bg-gradient-to-br ${GRADIENTS.background.primary} p-4`}>
			<Toaster position='top-right' />

			{/* Caption below navbar */}
			{currentStep === 0 && !loading && (
				<div className='text-center text-base font-medium text-blue-800 mb-4'>
					Join the Necttos community today – Register now! Need a hand?{' '}
					<button type='button' onClick={() => setShowGuide(true)} className='underline text-blue-600 hover:text-blue-900'>
						Click here →
					</button>
				</div>
			)}

			<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`w-full max-w-4xl ${COLORS.background.glass} backdrop-blur-lg rounded-2xl shadow-xl p-8 border ${COLORS.border.glass}`}>
				{/* Progress Bar */}
				<div className='flex justify-between mb-8'>
					{steps.map((step, idx) => (
						<div key={idx} className='flex flex-col items-center w-full'>
							<div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-500 text-white text-sm font-bold mb-2`}>{idx + 1}</div>
							<div className={`text-sm ${idx <= currentStep ? COLORS.text.primary : 'text-gray-400'} hidden sm:block`}>{step.title}</div>
							{idx < steps.length - 0 && <div className={`h-0.5 w-full mt-4 ${idx < currentStep ? 'bg-blue-600' : 'bg-gray-300'}`} />}
						</div>
					))}
				</div>

				{/* Form Fields */}
				<motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className='space-y-6'>
					<h2 className={`text-2xl font-bold ${COLORS.text.primary} mb-6`}>{steps[currentStep].title}</h2>

					{steps[currentStep].fields.map((field) => (
						<div key={field.name}>
							<label className={`block text-sm font-bold ${COLORS.text.secondary}`}>{field.label}</label>

							{field.type === 'select' ? (
								<div className='grid grid-cols-1 sm:grid-cols-2'>
									{field.options.map((option, idx) => (
										<label key={idx} className='flex items-center space-x-2 text-sm text-gray-800'>
											<input type='checkbox' name={field.name} value={option} checked={formData[field.name]?.includes(option)} onChange={handleInputChange} className='accent-blue-600 w-4 h-4 mt-4' />
											<span>{option}</span>
										</label>
									))}
								</div>
							) : field.type === 'file' ? (
								<div className='relative'>
									{/* Hidden file input */}
									<input type='file' name={field.name} accept={field.accept} onChange={handleInputChange} className='absolute inset-0 opacity-0 cursor-pointer' id={field.name} />
									{/* Button with preview and label */}
									<button type='button' onClick={() => document.getElementById(field.name).click()} className='w-full px-4 py-2 rounded-lg bg-gradient-to-r focus:outline-none flex items-center justify-start space-x-4' style={{ minHeight: '50px' }}>
										{formData.logoPreview && <img src={formData.logoPreview} alt='Logo Preview' className='h-10 w-10 object-contain rounded' />}
										<span>{formData.logo ? 'Logo uploaded' : field.buttonText || 'Upload Logo'}</span>
									</button>

									{errors[field.name] && <p className='text-red-500 text-sm mt-1'>{errors[field.name]}</p>}
								</div>
							) : (
								<input type={field.type} name={field.name} value={formData[field.name] || ''} onChange={handleInputChange} className={`w-full px-4 py-2 rounded-lg ${COLORS.background.glass} border ${COLORS.border.glass} focus:outline-none focus:ring-2 focus:ring-blue-500 ${COLORS.text.primary}`} required />
							)}
						</div>
					))}
				</motion.div>

				{/* Navigation Buttons */}
				<div className='flex justify-between mt-8'>
					<button
						onClick={() => setCurrentStep((prev) => prev - 1)}
						disabled={currentStep === 0}
						className={`px-6 py-2 rounded-lg ${currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'} 
              bg-gradient-to-r ${GRADIENTS.button.secondary} text-white transition-all duration-300`}>
						Back
					</button>
					<button
						onClick={currentStep === steps.length - 1 ? handleSubmit : nextStep}
						disabled={loading}
						className={`px-6 py-2 rounded-lg hover:opacity-90 bg-gradient-to-r ${GRADIENTS.button.primary} 
              text-white transition-all duration-300 flex items-center space-x-2`}>
						{loading ? <span className='inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white' /> : <span>{currentStep === steps.length - 1 ? 'Submit' : 'Next'}</span>}
					</button>
				</div>
			</motion.div>

			{showGuide && (
				<div
					className='fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4'
					onClick={() => setShowGuide(false)} // Close on outside click
				>
					<div
						className='bg-white max-w-2xl w-full rounded-xl shadow-lg p-6 overflow-y-auto max-h-[90vh] relative'
						onClick={(e) => e.stopPropagation()} // Prevent close on inside click
					>
						<h2 className='text-xl font-bold text-gray-800 mb-4'>College Registration Form Guide</h2>
						<div className='space-y-4 text-gray-700 text-sm sm:text-base'>
							<div>
								<h3 className='text-lg font-semibold text-gray-800 mb-2'>Basic Information</h3>
								<ul className='space-y-1 list-decimal'>
									<li>Name of the Institution</li>
									<li>Short name of the institution</li>
									<li>Enter the slogan of the institution</li>
									<li>Established year of the institution</li>
									<li>Enter the Name of the founder</li>
									<li>Enter the Name of Principal</li>
									<li>Accreditation Grade</li>
									<li>University of affiliation</li>
									<li>Upload Logo of the institution (.PNG)</li>
								</ul>
							</div>

							<div>
								<h3 className='text-lg font-semibold text-gray-800 mb-2'>Location & Contact Details</h3>
								<ul className='space-y-1 list-decimal'>
									<li>Contact number (institution use only)</li>
									<li>Email address</li>
									<li>Website URL</li>
									<li>Address</li>
									<li>District</li>
									<li>Pincode</li>
									<li>Area of the institution</li>
									<li>
										Campus type:
										<ol>
											<li>▢ Urban</li>
											<li>▢ Rural</li>
										</ol>
									</li>
								</ul>
							</div>

							<div>
								<h3 className='text-lg font-semibold text-gray-800 mb-2'>Education Details</h3>
								<ul className='space-y-1 list-decimal'>
									<li>
										Organizational body:
										<ol>
											<li>▢ Trust</li>
											<li>▢ Government</li>
											<li>▢ Co-operate</li>
										</ol>
									</li>
									<li>
										Institution type:
										<ol>
											<li>▢ Aided</li>
											<li>▢ Self-financing</li>
											<li>▢ Government</li>
										</ol>
									</li>
									<li>
										Affiliation type:
										<ol>
											<li>▢ Affiliated</li>
											<li>▢ Autonomous</li>
											<li>▢ University</li>
										</ol>
									</li>
									<li>
										Gender type:
										<ol>
											<li>▢ Mixed</li>
											<li>▢ Boys</li>
											<li>▢ Girls</li>
										</ol>
									</li>
									<li>
										Levels:
										<ol>
											<li>▢ Kindergarden</li>
											<li>▢ Lower Primary</li>
											<li>▢ Upper Primary</li>
											<li>▢ High School</li>
											<li>▢ Higher Secondary Education</li>
											<li>▢ Under Graduation</li>
											<li>▢ Post Graduation</li>
											<li>▢ Doctor of Philosophy</li>
											<li>▢ Diploma</li>
											<li>▢ Teacher Education</li>
										</ol>
									</li>
									<li>
										Categories:
										<ol>
											<li>▢ Arts and Science</li>
											<li>▢ Engineering</li>
											<li>▢ Polytechnic</li>
											<li>▢ Medical</li>
											<li>▢ Law</li>
											<li>▢ Commerce</li>
											<li>▢ ITI</li>
										</ol>
									</li>
								</ul>
							</div>

							<div>
								<h3
									className='text-lg font-semibold text-gray-800 mb-2
    '>
									Verification
								</h3>
								<ul className='space-y-1 list-decimal'>
									<li>Please enter the OTP sent to your registered mobile number</li>
								</ul>
							</div>

							<div>
								<p className='text-md-center italic font-thin text-gray-800'>Note: "Ensure data is entered accurately and be aware of the next required input."</p>
							</div>
						</div>

						<button onClick={() => setShowGuide(false)} className='absolute top-3 right-4 text-gray-500 hover:text-red-600 text-lg'>
							&times;
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export default RegisterForm;
